// /:partnerId/pay — pantalla de cobro hosteada del plan que el usuario eligió.
//
// Dos formas de llegar:
//   A) Link de pago del email: `/pay?pt=<payToken>`. NO requiere login. El token
//      es una credencial acotada (solo paga ESA suscripción; no da acceso a la
//      cuenta ni al contenido) y es compartible — pensado para que un familiar
//      pague. La pantalla muestra de quién es la cuenta y qué plan se paga
//      (anti-estafa). Redimir el link marca el email como verificado.
//   B) Sin `pt`: fallback con login email+password (self-OAuth Auth Code + PKCE)
//      → access_token propio para leer /me/subscription y pagar.
//
// Flujo de pago (común a ambos), según la pasarela del partner:
//   Line:  pay (plan + DNI) → checkout → widget (iframe; postMessage del
//          resultado) → confirming (fetchRecurringToken + activa) → done, o
//          verifying si el cobro todavía no se pudo confirmar, o de vuelta a
//          pay con el aviso si la pasarela lo rechazó.
//   MercadoPago: pay (plan, sin DNI) → checkout → to-gateway (redirect a su
//          checkout) → vuelve a `/pay?mp_return=1` → confirming (el backend
//          consulta a MP y activa si el primer cobro ya se aprobó) → done, o
//          processing si MP todavía no cobró (se activa solo por webhook).
//
// Retorno a la app: si el alta vino de un flow OAuth, al confirmar el pago el
// backend mintea un authorization_code y devuelve `redirect_to` → el usuario
// vuelve logueado. Los authz params NO se leen de la URL: los deja la pantalla
// anterior (/signup o /verify) en sessionStorage, atados a este `pt`. El por qué
// —y qué se pierde— está en lib/pay-return.ts.
//
// Un pago del widget NUNCA se descarta sin confirmarlo antes. El `payment_id`
// se guarda (lib/checkout-resume.ts) apenas lo devuelve el checkout: si la
// página se recarga —en iPhone, el salto a la app del banco descarta la
// pestaña— lo primero que se hace al volver es confirmar ESE pago, y recién si
// no está aprobado se ofrece pagar. Lo mismo antes de cualquier "volver a
// empezar". Crear un checkout nuevo con uno aprobado sin confirmar es
// exactamente cómo se le cobraba dos veces a la gente.

import { FormEvent, useEffect, useRef, useState } from 'react';
import { normalizeEmail } from '@/lib/email-normalize';
import { useParams, useSearchParams } from 'react-router-dom';
import AuthShell from '@/components/AuthShell';
import BrandFooter from '@/components/BrandFooter';
import { BackStepLink, FlowExitLink } from '@/components/FlowNav';
import FormField from '@/components/FormField';
import SubmitButton from '@/components/SubmitButton';
import { resolvePalette, useTheme } from '@/theme/ThemeProvider';
import { lineWidgetOrigin, paletteToLineTheme, withLineTheme } from '@/lib/lineTheme';
import {
  loginForAccessToken,
  fetchMeSubscription,
  fetchPayContext,
  createCheckoutSession,
  confirmCheckoutPayment,
  redeemCoupon,
  ApiError,
  type CheckoutAuth,
  type MeSubscription,
  type PayContext,
  type VerifyAuthzParams,
} from '@/lib/api';
import { couponErrorMessage } from '@/pages/Subscribe';
import { precheckCoupon } from '@/lib/couponCheck';
import { clearPayReturn, readPayReturn } from '@/lib/pay-return';
import PaymentVerifying from '@/components/PaymentVerifying';
import { clearCheckoutResume, readCheckoutResume, resumeKey, saveCheckoutResume } from '@/lib/checkout-resume';
import {
  CONFIRM_DELAYS,
  GATEWAY_CONFIRM_DELAYS,
  PAYMENT_REJECTED_MESSAGE,
  RESUME_CONFIRM_DELAYS,
  SINGLE_CONFIRM,
  checkoutOutcome,
  pollConfirm,
} from '@/lib/checkout-outcome';

// Client first-party del portal para el self-OAuth (solo en el fallback con login).
const PORTAL_CLIENT_ID =
  (import.meta.env.VITE_PORTAL_CLIENT_ID as string | undefined) ?? 'cli_dev_spa';

interface PlanView {
  plan_id: string;
  display_name: string;
  price_amount: number;
  price_currency: string;
  billing_period: 'monthly' | 'yearly' | 'weekly';
  features?: Array<string | { label: string; included?: boolean }>;
}

// Datos del titular para el header anti-estafa (solo en el flujo pay-token).
interface Holder {
  name: string | null;
  email: string | null;
}

// Pasarelas que se pagan fuera del portal (redirect) en vez de en un widget.
const REDIRECT_PROVIDERS: ReadonlySet<string> = new Set(['mercadopago']);

// Lo que hace falta para volver a pintar el paso de pago desde cualquier otro.
interface PayView {
  plan: PlanView;
  holder?: Holder | undefined;
  provider?: string | null | undefined;
}

type Step =
  | { kind: 'login' }
  | { kind: 'loading' }
  | ({ kind: 'pay' } & PayView)
  | { kind: 'to-gateway' }
  | { kind: 'processing' }
  | ({ kind: 'widget'; checkoutUrl: string; paymentId: string } & PayView)
  // checking: no viene del aviso de éxito del widget, estamos averiguando.
  | { kind: 'confirming'; checking?: boolean }
  // Hay un pago sin veredicto: se verifica ESE pago, no se crea otro.
  | { kind: 'verifying'; view: PayView; paymentId: string; origin: 'resume' | 'timeout' }
  | { kind: 'redirecting' }
  | { kind: 'done'; planName: string; periodEnd: string | null }
  | { kind: 'already-active'; planName: string }
  // canRetry: error de pago (no terminal) → reintentamos re-derivando el estado.
  // inFlight: el pago que hay que confirmar antes de reintentar.
  | { kind: 'error'; message: string; canRetry?: boolean; inFlight?: { view: PayView; paymentId: string } };

function formatPrice(amount: number, currency: string): string {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

function planFromSub(sub: MeSubscription): PlanView {
  return {
    plan_id: sub.plan.plan_id,
    display_name: sub.plan.display_name,
    price_amount: sub.plan.price_amount,
    price_currency: sub.plan.price_currency,
    billing_period: sub.plan.billing_period,
    features: sub.plan.features,
  };
}

function planFromContext(ctx: PayContext): PlanView {
  return {
    plan_id: ctx.plan.plan_id,
    display_name: ctx.plan.display_name,
    price_amount: ctx.plan.price_amount,
    price_currency: ctx.plan.price_currency,
    billing_period: ctx.plan.billing_period,
  };
}

const LIVE_STATUSES: ReadonlyArray<MeSubscription['status']> = ['active', 'trialing', 'grace_period'];

export default function Pay(): JSX.Element {
  const { partnerId = '' } = useParams<{ partnerId: string }>();
  const [searchParams] = useSearchParams();
  const payToken = searchParams.get('pt');
  // Vuelta de una pasarela con redirect (MP): el back_url que armamos en
  // startPayment trae `mp_return=1`; MP le agrega `preapproval_id`.
  const gatewayReturn = searchParams.get('mp_return') === '1';
  const gatewayRef = searchParams.get('preapproval_id') ?? 'gateway-return';
  // Branding para tematizar el widget de Line (ver bloque `widget` abajo).
  const { state: brandingState, colorScheme } = useTheme();

  // Contexto de retorno al flow OAuth, dejado por /signup o /verify para ESTE
  // `pt`. Se lee una sola vez al montar (initializer lazy): la pestaña ya lo
  // tiene o no lo tiene, y no cambia durante el pago.
  const [authz] = useState<VerifyAuthzParams | undefined>(() => readPayReturn(payToken));
  const oauthRedirectUri = authz?.redirect_uri ?? null;
  const oauthState = authz?.state ?? null;

  const [step, setStep] = useState<Step>(payToken ? { kind: 'loading' } : { kind: 'login' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [customerDoc, setCustomerDoc] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Credencial activa del checkout: pay-token (link) o bearer (tras login).
  const authRef = useRef<CheckoutAuth | null>(payToken ? { payToken } : null);
  // Clave del pago en curso guardado: atada al `pt` o, con login, a la
  // suscripción de la sesión (se completa en loadSubscription).
  const resumeKeyRef = useRef<string | null>(resumeKey({ partnerId, pt: payToken }));
  // Guard contra doble envío de "Activar": `disabled={loading}` recién aplica
  // en el render siguiente, y dos taps seguidos crean dos pagos.
  const startingRef = useRef(false);

  // --- Carga inicial vía pay-token (sin login) ---
  async function loadFromPayToken(pt: string): Promise<void> {
    setStep({ kind: 'loading' });
    try {
      const ctx = await fetchPayContext(pt);
      const view: PayView = {
        plan: planFromContext(ctx),
        holder: { name: ctx.holder_name, email: ctx.holder_email },
        provider: ctx.payment_provider ?? null,
      };
      if (gatewayReturn && (ctx.status === 'pending' || LIVE_STATUSES.includes(ctx.status))) {
        await confirmPayment(view, gatewayRef, { gateway: true });
        return;
      }
      if (LIVE_STATUSES.includes(ctx.status)) {
        clearCheckoutResume(resumeKeyRef.current);
        setStep({ kind: 'already-active', planName: ctx.plan.display_name });
        return;
      }
      if (ctx.status === 'pending') {
        await enterPayStep(view);
        return;
      }
      setStep({
        kind: 'error',
        message: `Esta suscripción está en estado "${ctx.status}" y no se puede pagar desde este link.`,
      });
    } catch (e) {
      setStep({
        kind: 'error',
        message: e instanceof ApiError ? e.message : 'No pudimos abrir el link de pago.',
      });
    }
  }

  // --- Carga vía sesión (tras login email+password) ---
  async function loadSubscription(token: string): Promise<void> {
    setStep({ kind: 'loading' });
    try {
      const sub = await fetchMeSubscription(token);
      if (!sub) {
        setStep({ kind: 'error', message: 'No encontramos una suscripción para tu cuenta.' });
        return;
      }
      resumeKeyRef.current = resumeKey({ partnerId, subject: sub.subscription_id });
      const view: PayView = { plan: planFromSub(sub), provider: sub.payment_provider ?? null };
      if (gatewayReturn && (sub.status === 'pending' || LIVE_STATUSES.includes(sub.status))) {
        await confirmPayment(view, gatewayRef, { gateway: true });
        return;
      }
      if (LIVE_STATUSES.includes(sub.status)) {
        clearCheckoutResume(resumeKeyRef.current);
        setStep({ kind: 'already-active', planName: sub.plan.display_name });
        return;
      }
      if (sub.status === 'pending') {
        await enterPayStep(view);
        return;
      }
      setStep({
        kind: 'error',
        message: `Tu suscripción está en estado "${sub.status}". Volvé a registrarte o elegí un plan.`,
      });
    } catch (e) {
      setStep({
        kind: 'error',
        message: e instanceof ApiError ? e.message : 'Error cargando tu suscripción.',
      });
    }
  }

  // Puerta de entrada al paso de pago. Si quedó un pago en curso guardado (la
  // página se recargó con el widget abierto), se confirma ESE pago antes de
  // ofrecer pagar: puede estar aprobado.
  async function enterPayStep(view: PayView): Promise<void> {
    const saved = readCheckoutResume(resumeKeyRef.current);
    if (saved) {
      await confirmPayment(view, saved.paymentId, { origin: 'resume' });
      return;
    }
    setStep({ kind: 'pay', ...view });
  }

  // Re-deriva el estado desde el server (reintento). Branch según la credencial.
  async function reload(): Promise<void> {
    const auth = authRef.current;
    if (auth && 'payToken' in auth) {
      await loadFromPayToken(auth.payToken);
    } else if (auth && 'bearer' in auth) {
      await loadSubscription(auth.bearer);
    } else {
      setStep({ kind: 'login' });
    }
  }

  // Carga inicial: si vino pay-token, resolvemos el contexto al montar.
  useEffect(() => {
    if (payToken) void loadFromPayToken(payToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payToken]);

  // Aviso para devs: sin contexto de retorno, al confirmar el pago NO se
  // redirige a la app (pantalla final "volvé a la app"). Mejor avisar en la
  // consola del browser que degradar mudo — así el integrador se autodiagnostica
  // sin pedirnos los logs de CloudWatch.
  useEffect(() => {
    if (authz) return;
    console.warn(
      '[pay] Sin contexto de retorno para este pago: al confirmar NO se redirige ' +
        'automáticamente a la app. Pasa cuando el alta no vino de un flow OAuth ' +
        '(sin client_id/redirect_uri/state/code_challenge), o cuando /pay se abrió ' +
        'en otra pestaña o navegador que el del registro.',
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mandamos contexto de retorno pero el server no minteó el code: la causa
  // accionable la dice el propio server (`redirect_uri_ok: false` = el
  // redirect_uri no está registrado en el client). Espejo del `return_url_ok`
  // de /subscribe: el integrador lo ve en DevTools sin pedirnos CloudWatch.
  function warnIfNoRedirect(res: { redirect_uri_ok?: boolean }): void {
    if (!authz) return;
    if (res.redirect_uri_ok === false) {
      console.warn(
        `[pay] El redirect_uri "${authz.redirect_uri}" no está registrado en el ` +
          `client "${authz.client_id}": el pago se activó pero NO se redirige a la app. ` +
          'Registralo (backoffice → partner → clients, match EXACTO del string).',
      );
      return;
    }
    console.warn(
      '[pay] El pago se activó pero el server no devolvió redirect_to: no se ' +
        'redirige a la app. Revisar los logs de subscriptions-checkout.',
    );
  }

  async function onLogin(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setLoading(true);
    setErr(null);
    try {
      const token = await loginForAccessToken({
        partnerId,
        email: normalizeEmail(email),
        password,
        clientId: PORTAL_CLIENT_ID,
        redirectUri: `${window.location.origin}/${partnerId}/pay`,
      });
      authRef.current = { bearer: token };
      await loadSubscription(token);
    } catch (e) {
      const apiErr = e instanceof ApiError ? e : null;
      setErr(
        apiErr?.code === 'invalid_credentials' || apiErr?.status === 401
          ? 'Email o contraseña incorrectos.'
          : apiErr?.message ?? 'No pudimos iniciar sesión. Probá de nuevo.',
      );
    } finally {
      setLoading(false);
    }
  }

  async function startPayment(view: PayView): Promise<void> {
    if (startingRef.current) return;
    startingRef.current = true;
    try {
      await runStartPayment(view);
    } finally {
      startingRef.current = false;
    }
  }

  async function runStartPayment(view: PayView): Promise<void> {
    const { plan, provider } = view;
    const auth = authRef.current;
    if (!auth) {
      setErr('Sesión no válida. Volvé a abrir el link.');
      return;
    }
    // Con cupón NO se pide documento ni se abre el widget: el canje reemplaza
    // el pago (el documento solo lo exige Line).
    if (couponCode.trim()) {
      // Verificación local (formato + dígito verificador) antes de la API.
      const code = precheckCoupon(couponCode);
      if (!code) {
        setErr('Código no válido. Revisá que lo hayas copiado bien.');
        return;
      }
      setLoading(true);
      setErr(null);
      try {
        const res = await redeemCoupon(auth, plan.plan_id, code, authz);
        if (res.redirect_to) {
          setStep({ kind: 'redirecting' });
          clearPayReturn();
          window.location.href = res.redirect_to;
          return;
        }
        warnIfNoRedirect(res);
        clearCheckoutResume(resumeKeyRef.current);
        setStep({ kind: 'done', planName: plan.display_name, periodEnd: res.current_period_end });
      } catch (e) {
        setErr(couponErrorMessage(e));
      } finally {
        setLoading(false);
      }
      return;
    }
    // El documento solo lo exige Line; las pasarelas con redirect lo piden en
    // su propio checkout.
    const isRedirect = provider ? REDIRECT_PROVIDERS.has(provider) : false;
    const doc = customerDoc.replace(/\D/g, '');
    if (!isRedirect && doc.length < 6) {
      setErr('Ingresá un número de documento válido (solo números).');
      return;
    }
    setLoading(true);
    setErr(null);
    try {
      // A dónde vuelve el usuario desde la pasarela: esta misma pantalla, con
      // el `pt` (el contexto de retorno a la app vive en sessionStorage atado a
      // él, y sobrevive al ir y volver en la misma pestaña).
      const back = new URL(`${window.location.origin}/${partnerId}/pay`);
      if (payToken) back.searchParams.set('pt', payToken);
      back.searchParams.set('mp_return', '1');
      const session = await createCheckoutSession(
        auth,
        plan.plan_id,
        isRedirect ? {} : { document: doc },
        { successUrl: back.toString(), authz },
      );
      const outcome = checkoutOutcome(session, REDIRECT_PROVIDERS);
      if (outcome.kind === 'activated') {
        // Un pago anterior ya estaba aprobado: no hay nada que pagar.
        finishActivated(plan, outcome.res);
        return;
      }
      if (outcome.kind === 'redirect') {
        setStep({ kind: 'to-gateway' });
        window.location.href = outcome.url;
        return;
      }
      if (outcome.kind === 'invalid') {
        setErr('No pudimos iniciar el pago. Probá de nuevo en unos minutos.');
        return;
      }
      // Se guarda ANTES de mostrar el widget: desde acá el pago puede aprobarse
      // sin que esta pestaña llegue a enterarse.
      saveCheckoutResume(resumeKeyRef.current, {
        paymentId: outcome.paymentId,
        planId: plan.plan_id,
        checkoutUrl: outcome.checkoutUrl,
      });
      setStep({
        kind: 'widget',
        ...view,
        checkoutUrl: outcome.checkoutUrl,
        paymentId: outcome.paymentId,
      });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'No pudimos iniciar el pago.');
    } finally {
      setLoading(false);
    }
  }

  // Activación confirmada (por el confirm o por el checkout mismo): vuelve a la
  // app si el alta vino de un flow OAuth, o muestra la pantalla final.
  function finishActivated(
    plan: PlanView,
    res: { redirect_to?: string; redirect_uri_ok?: boolean },
  ): void {
    clearCheckoutResume(resumeKeyRef.current);
    // Si vino del flow OAuth, el backend minteó el code → volvemos a la OTT.
    if (res.redirect_to) {
      setStep({ kind: 'redirecting' });
      clearPayReturn();
      window.location.href = res.redirect_to;
      return;
    }
    warnIfNoRedirect(res);
    setStep({ kind: 'done', planName: plan.display_name, periodEnd: null });
  }

  // La pasarela rechazó el pago: ya no hay nada que confirmar. Vuelve al paso
  // de pago con el aviso.
  function showRejected(view: PayView): void {
    clearCheckoutResume(resumeKeyRef.current);
    setErr(PAYMENT_REJECTED_MESSAGE);
    setStep({ kind: 'pay', ...view });
  }

  // Confirma el pago: sondea hasta un veredicto (activado o rechazado). Si se
  // agota sin veredicto NO ofrece pagar de nuevo: pasa a `verifying`, que
  // vuelve a consultar el MISMO pago.
  async function confirmPayment(
    view: PayView,
    paymentId: string,
    opts: { gateway?: boolean; origin?: 'resume' | 'timeout' } = {},
  ): Promise<void> {
    const auth = authRef.current;
    if (!auth) {
      setStep({ kind: 'error', message: 'Sesión no válida. Volvé a abrir el link.' });
      return;
    }
    setStep({ kind: 'confirming', checking: opts.origin === 'resume' });
    // MP puede tardar en aprobar el primer cobro: se espera más, y si no
    // llega no es un error (el webhook lo activa después).
    const delays = opts.gateway
      ? GATEWAY_CONFIRM_DELAYS
      : opts.origin === 'resume'
        ? RESUME_CONFIRM_DELAYS
        : CONFIRM_DELAYS;
    const result = await pollConfirm(
      () => confirmCheckoutPayment(auth, view.plan.plan_id, paymentId, authz),
      delays,
    );
    if (result.kind === 'activated') {
      finishActivated(view.plan, result.res);
      return;
    }
    if (result.kind === 'rejected') {
      showRejected(view);
      return;
    }
    if (result.kind === 'unauthorized') {
      setStep({ kind: 'error', message: 'Tu sesión expiró. Volvé a ingresar.' });
      return;
    }
    if (opts.gateway) {
      setStep({ kind: 'processing' });
      return;
    }
    setStep({ kind: 'verifying', view, paymentId, origin: opts.origin ?? 'timeout' });
  }

  // "Volver a empezar" / "Pagar con otra tarjeta" / reintento tras un fallo del
  // widget: antes de soltar el pago en curso se lo consulta una vez. Solo si no
  // está aprobado se reinicia.
  async function restartSafely(view: PayView, paymentId: string): Promise<void> {
    const auth = authRef.current;
    if (!auth) {
      setStep({ kind: 'login' });
      return;
    }
    setStep({ kind: 'confirming', checking: true });
    const result = await pollConfirm(
      () => confirmCheckoutPayment(auth, view.plan.plan_id, paymentId, authz),
      SINGLE_CONFIRM,
    );
    if (result.kind === 'activated') {
      finishActivated(view.plan, result.res);
      return;
    }
    if (result.kind === 'rejected') {
      showRejected(view);
      return;
    }
    if (result.kind === 'unauthorized') {
      setStep({ kind: 'error', message: 'Tu sesión expiró. Volvé a ingresar.' });
      return;
    }
    // Sin aprobar: el usuario pidió empezar de nuevo. Si ese pago igual se
    // aprueba después, el checkout siguiente lo detecta y activa sin cobrar.
    clearCheckoutResume(resumeKeyRef.current);
    setErr(null);
    await reload();
  }

  // Chequeo de fondo de la pantalla `verifying`: una consulta silenciosa que
  // solo actúa si hay veredicto.
  async function backgroundCheck(view: PayView, paymentId: string): Promise<void> {
    const auth = authRef.current;
    if (!auth) return;
    const result = await pollConfirm(
      () => confirmCheckoutPayment(auth, view.plan.plan_id, paymentId, authz),
      SINGLE_CONFIRM,
    );
    if (result.kind === 'activated') finishActivated(view.plan, result.res);
    else if (result.kind === 'rejected') showRejected(view);
  }

  // Escucha el resultado del widget (postMessage). El origin esperado sale de
  // la checkout_url del backend (staging/prod según la config del provider).
  useEffect(() => {
    if (step.kind !== 'widget') return;
    const expectedOrigin = lineWidgetOrigin(step.checkoutUrl);
    function onMessage(event: MessageEvent): void {
      if (event.origin !== expectedOrigin) return;
      const type = (event.data as { type?: string })?.type;
      if (step.kind !== 'widget') return;
      const view: PayView = { plan: step.plan, holder: step.holder, provider: step.provider };
      if (type === 'line-checkout:payment-success') {
        void confirmPayment(view, step.paymentId);
      } else if (type === 'line-checkout:payment-failure') {
        setStep({
          kind: 'error',
          message: 'El pago no pudo completarse. Podés reintentar con la misma u otra tarjeta.',
          canRetry: true,
          // El reintento confirma este pago antes de abrir otro.
          inFlight: { view, paymentId: step.paymentId },
        });
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.kind]);

  // ----- Componentes auxiliares -----

  // Header anti-estafa: deja claro de quién es la cuenta y qué se paga.
  function HolderBanner({ holder, plan }: { holder: Holder; plan: PlanView }): JSX.Element {
    return (
      <div className="rounded-lg border border-primary/40 bg-primary/10 p-3 text-sm text-text">
        Estás pagando la suscripción
        {holder.name ? (
          <>
            {' '}de <strong>{holder.name}</strong>
          </>
        ) : (
          ' de esta cuenta'
        )}
        {holder.email ? <> ({holder.email})</> : null}: plan{' '}
        <strong>{plan.display_name}</strong> por{' '}
        <strong>{formatPrice(plan.price_amount, plan.price_currency)}</strong>
        {plan.billing_period === 'monthly' ? '/mes' : ''}.
        <div className="mt-1 text-xs text-text-muted">
          Si no reconocés esta cuenta, no completes el pago.
        </div>
      </div>
    );
  }

  // ----- Render por estado -----

  if (step.kind === 'login') {
    return (
      <AuthShell
        title="Completá tu pago"
        subtitle="Ingresá para activar tu plan."
        flowExit={
          <FlowExitLink
            exit={
              oauthRedirectUri
                ? { kind: 'oauth', redirectUri: oauthRedirectUri, state: oauthState }
                : null
            }
          />
        }
      >
        <form onSubmit={onLogin} className="space-y-4" noValidate aria-busy={loading || undefined}>
          <FormField
            name="email"
            type="email"
            label="Email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <FormField
            name="password"
            type="password"
            label="Contraseña"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {err ? (
            <div role="alert" className="rounded-lg border border-error/30 bg-error/10 p-3 text-sm text-error">
              {err}
            </div>
          ) : null}
          <SubmitButton loading={loading} loadingLabel="Ingresando…">
            Continuar
          </SubmitButton>
        </form>
      </AuthShell>
    );
  }

  if (step.kind === 'loading') {
    return (
      <AuthShell title="Un momento…" subtitle="Cargando los datos del pago.">
        <div className="text-center text-sm text-text-muted py-4">⏳</div>
      </AuthShell>
    );
  }

  if (step.kind === 'already-active') {
    return (
      <AuthShell title="Ya está al día ✓" subtitle={`El plan ${step.planName} está activo.`}>
        <div className="rounded-lg border border-primary/30 bg-primary/10 p-4 text-sm text-text">
          No hay pagos pendientes. Volvé a la app para disfrutar del contenido.
        </div>
      </AuthShell>
    );
  }

  if (step.kind === 'pay') {
    const { plan, holder, provider } = step;
    const needsDocument = !(provider && REDIRECT_PROVIDERS.has(provider));
    return (
      <AuthShell
        title={`Activá ${plan.display_name}`}
        subtitle="Un último paso para empezar."
        flowExit={
          <FlowExitLink
            exit={
              oauthRedirectUri
                ? { kind: 'oauth', redirectUri: oauthRedirectUri, state: oauthState }
                : null
            }
          />
        }
      >
        <div className="space-y-4">
          {holder ? <HolderBanner holder={holder} plan={plan} /> : null}
          <div className="rounded-xl border border-border p-4">
            <div className="text-sm text-text-muted">{plan.display_name}</div>
            <div className="mt-1 text-2xl font-semibold text-text">
              {formatPrice(plan.price_amount, plan.price_currency)}
              <span className="text-sm text-text-muted">
                {plan.billing_period === 'monthly' ? '/mes' : ''}
              </span>
            </div>
            {plan.features && plan.features.length > 0 ? (
              <ul className="mt-3 space-y-1.5">
                {plan.features.map((f, i) => {
                  // included:false = no viene con el plan. Ver PlanPicker.
                  const included = typeof f === 'string' || f.included !== false;
                  return (
                    <li
                      key={i}
                      className={`flex items-start gap-2 text-sm ${
                        included ? 'text-text' : 'text-text-muted'
                      }`}
                    >
                      <span
                        aria-hidden
                        className={`mt-0.5 ${included ? 'text-primary' : 'text-text-muted'}`}
                      >
                        {included ? '✓' : '✕'}
                      </span>
                      <span className={included ? undefined : 'line-through'}>
                        {typeof f === 'string' ? f : f.label}
                      </span>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
          {needsDocument ? (
            <FormField
              name="document"
              type="text"
              inputMode="numeric"
              label="Número de documento"
              autoComplete="off"
              required
              value={customerDoc}
              onChange={(e) => setCustomerDoc(e.target.value)}
            />
          ) : null}
          <FormField
            name="coupon"
            type="text"
            label="Cupón (opcional)"
            autoComplete="off"
            value={couponCode}
            onChange={(e) => {
              setCouponCode(e.target.value);
              setErr(null);
            }}
          />
          {err ? (
            <div role="alert" className="rounded-lg border border-error/30 bg-error/10 p-3 text-sm text-error">
              {err}
            </div>
          ) : null}
          <SubmitButton
            type="button"
            onClick={() => void startPayment({ plan, holder, provider })}
            loading={loading}
            loadingLabel={couponCode.trim() ? 'Validando el cupón…' : 'Abriendo el pago…'}
          >
            {couponCode.trim() ? 'Validar cupón' : 'Activar'}
          </SubmitButton>
        </div>
      </AuthShell>
    );
  }

  if (step.kind === 'widget') {
    // Layout anclado a la ventana (fixed inset-0): el header (título + banner
    // anti-estafa) queda fijo arriba y el footer fijo abajo, en términos
    // absolutos de pantalla. El iframe de Line (flex-1 + min-h-0, SIN
    // minHeight) absorbe exactamente el alto restante y es el ÚNICO que
    // scrollea — internamente, con su propio scroll. Así el botón de pagar
    // (que vive al final del contenido del iframe) siempre es alcanzable y no
    // hay doble scroll (página + componente) que lo esconda en pantallas
    // cortas. Line no emite evento de altura, por eso no dimensionamos el
    // iframe al contenido: lo dejamos llenar el hueco y delegamos el scroll.
    //
    // Tema: el widget hereda el branding del partner vía query params, con la
    // paleta ACTIVA (clara u oscura, la misma que pinta el portal) — Line ya
    // pinta el body del documento con `backgroundColor` (ver lineTheme.ts).
    // El iframe va `bg-background` para que antes de cargar no haya flash de
    // otro color; una vez cargado, el body del widget queda del mismo tono.
    const checkoutSrc =
      brandingState.status === 'ready'
        ? withLineTheme(
            step.checkoutUrl,
            paletteToLineTheme(resolvePalette(brandingState.branding, colorScheme)),
          )
        : step.checkoutUrl;
    return (
      <main className="fixed inset-0 flex flex-col bg-background px-3 py-3 sm:px-4">
        <div className="mx-auto flex min-h-0 w-full max-w-md flex-1 flex-col gap-3">
          <header className="shrink-0 space-y-2">
            <h1 className="text-center text-lg font-semibold text-text">Pagá de forma segura</h1>
            {step.holder ? <HolderBanner holder={step.holder} plan={step.plan} /> : null}
          </header>
          <iframe
            title="Line Checkout"
            src={checkoutSrc}
            className="w-full min-h-0 flex-1 rounded-lg border border-border bg-background"
          />
          <div className="shrink-0 space-y-1.5">
            <div className="py-1 text-center">
              <BackStepLink
                onClick={() =>
                  void restartSafely(
                    { plan: step.plan, holder: step.holder, provider: step.provider },
                    step.paymentId,
                  )
                }
                label="¿Algo salió mal? Volver a empezar"
              />
            </div>
          <FlowExitLink
            exit={
              oauthRedirectUri
                ? { kind: 'oauth', redirectUri: oauthRedirectUri, state: oauthState }
                : null
            }
          />
            <BrandFooter />
          </div>
        </div>
      </main>
    );
  }

  if (step.kind === 'to-gateway') {
    return (
      <AuthShell title="Te llevamos al pago…" subtitle="Vas a completar el pago en MercadoPago.">
        <div className="text-center text-sm text-text-muted py-4">⏳</div>
      </AuthShell>
    );
  }

  if (step.kind === 'processing') {
    return (
      <AuthShell title="Estamos procesando tu pago" subtitle="Puede demorar unos minutos.">
        <div className="space-y-4">
          <div className="rounded-lg border border-primary/30 bg-primary/10 p-4 text-sm text-text">
            MercadoPago todavía no nos confirmó el cobro. Tu plan se activa solo en cuanto lo
            haga: no hace falta que vuelvas a pagar.
          </div>
          <SubmitButton type="button" onClick={() => void reload()} loading={loading} loadingLabel="Verificando…">
            Verificar de nuevo
          </SubmitButton>
        </div>
      </AuthShell>
    );
  }

  if (step.kind === 'confirming') {
    return (
      <AuthShell
        title={step.checking ? 'Verificando tu pago…' : 'Confirmando tu pago…'}
        subtitle="No cierres esta ventana."
      >
        <div className="text-center text-sm text-text-muted py-4">⏳</div>
      </AuthShell>
    );
  }

  if (step.kind === 'verifying') {
    const { view, paymentId } = step;
    return (
      <PaymentVerifying
        origin={step.origin}
        onVerify={() => void confirmPayment(view, paymentId)}
        onPayAgain={() => void restartSafely(view, paymentId)}
        onBackgroundCheck={() => void backgroundCheck(view, paymentId)}
      />
    );
  }

  if (step.kind === 'redirecting') {
    return (
      <AuthShell title="¡Pago confirmado! ✓" subtitle="Te llevamos de vuelta a la app…">
        <div className="text-center text-sm text-text-muted py-4">⏳</div>
      </AuthShell>
    );
  }

  if (step.kind === 'done') {
    return (
      <AuthShell title="¡Listo! Tu plan quedó activo ✓" subtitle="Gracias por suscribirte.">
        <div className="rounded-lg border border-primary/30 bg-primary/10 p-4 text-sm text-text">
          El plan <strong>{step.planName}</strong> quedó activo. Volvé a la app para disfrutar del
          contenido.
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Hubo un problema" subtitle="No pudimos completar el pago.">
      <div role="alert" className="rounded-lg border border-error/30 bg-error/10 p-3 text-sm text-error">
        {step.message}
      </div>
      {step.canRetry ? (
        <SubmitButton
          type="button"
          onClick={() => {
            const inFlight = step.inFlight;
            if (inFlight) void restartSafely(inFlight.view, inFlight.paymentId);
            else void reload();
          }}
          loading={loading}
          loadingLabel="Reintentando…"
        >
          Reintentar el pago
        </SubmitButton>
      ) : null}
    </AuthShell>
  );
}
